import { useEffect, useState } from 'react';
import { CalendarRangeSheet, type DateRange } from '@/components/ui/calendar';
import type { StayWhenSheetProps } from '../types';

/**
 * Date-range sheet for the Stay booking flow.
 *
 * Differs from SunbedWhenSheet: the confirm button is disabled until the user
 * selects a range where check-out is strictly after check-in (minimum 1 night).
 * Same-day selection is not valid for stays.
 */
export function StayWhenSheet({
  isOpen,
  onClose,
  cmsProps,
  selectedFrom,
  selectedTo,
  onSelect,
}: StayWhenSheetProps) {
  const [range, setRange] = useState<DateRange | undefined>(
    selectedFrom ? { from: selectedFrom, to: selectedTo ?? undefined } : undefined
  );

  useEffect(() => {
    if (isOpen) {
      setRange(selectedFrom ? { from: selectedFrom, to: selectedTo ?? undefined } : undefined);
    }
  }, [isOpen]);

  // Valid only when both dates are set and they fall on different calendar days.
  const isValidRange = !!range?.from && !!range?.to && range.from.toDateString() !== range.to.toDateString();

  const handleConfirm = () => {
    if (!isValidRange) return;
    onSelect({ from: range!.from!, to: range!.to! });
    onClose();
  };

  const assistance =
    cmsProps?.assistanceTitle && cmsProps?.assistanceDescription
      ? {
          title: cmsProps.assistanceTitle,
          description: cmsProps.assistanceDescription,
          descriptionForMember: cmsProps.assistanceDescriptionForMember,
        }
      : undefined;

  return (
    <CalendarRangeSheet
      isOpen={isOpen}
      onClose={onClose}
      title={cmsProps?.title}
      subtitle={cmsProps?.subtitle}
      selected={range}
      onSelect={setRange}
      selectLabel={cmsProps?.selectButtonLabel}
      onConfirm={handleConfirm}
      confirmDisabled={!isValidRange}
      assistance={assistance}
    />
  );
}
